import {useEffect, useState, type ReactNode} from 'react';
import {useLocation} from 'react-router-dom';
import {Smartphone, X} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {POSTHOG_UNMASK_CLASS} from '@common-web/lib/analytics';
import {useEnrollGroup} from '@client/pages/public/enroll/enrollContext';
import {enrollBrandVars} from '@client/pages/public/enroll/brand';
import {EnrollEvent, useEnrollAnalytics} from '@client/pages/public/enroll/enrollAnalytics';
import {STEP} from '@client/pages/public/enroll/enrollModel';

// Clever Health mobile app — shared across groups (same App Store / Play listing for everyone).
export const APP_STORE_URL = 'https://apps.apple.com/us/app/id1549254810';
export const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.ventures317.cleverhealth';

/** A store link. `onLight` for use on a white surface, `onDark` on a dark one. */
export function StoreButton({
  label,
  href,
  variant = 'onDark',
  surface,
}: {
  label: string;
  href: string;
  variant?: 'onDark' | 'onLight';
  surface: 'banner' | 'modal';
}) {
  const {track} = useEnrollAnalytics();
  const store = href === APP_STORE_URL ? 'app_store' : 'play_store';
  const styles =
    variant === 'onLight'
      ? 'bg-[var(--enroll-ink)] text-white hover:opacity-90'
      : 'bg-white/10 text-white hover:bg-white/20';
  return (
    <a
      href={href}
      target='_blank'
      rel='noreferrer'
      onClick={() => track(EnrollEvent.AppDownloadClicked, {store, surface})}
      className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${styles}`}
    >
      <Smartphone className='h-4 w-4' />
      {label}
    </a>
  );
}

/** Full-width "download the app" banner (icon + copy left, store buttons right). */
export function AppDownloadBanner() {
  return (
    <section className={`flex flex-col items-start gap-4 rounded-xl border border-neutral-200 bg-card p-6 md:flex-row md:items-center md:justify-between ${POSTHOG_UNMASK_CLASS}`}>
      <div className='flex items-start gap-3'>
        <span className='inline-flex h-10 w-10 flex-none items-center justify-center rounded-full bg-[var(--enroll-accent)] text-white'>
          <Smartphone className='h-5 w-5' />
        </span>
        <div>
          <div className='font-semibold text-neutral-900'>
            Download the Clever Health mobile app
          </div>
          <p className='text-sm text-neutral-500'>
            View your member ID cards, start a virtual visit, and access prescription savings —
            right from your phone.
          </p>
        </div>
      </div>
      <div className='flex flex-none gap-3 self-end md:self-auto'>
        <StoreButton label='App Store' href={APP_STORE_URL} variant='onLight' surface='banner' />
        <StoreButton label='Google Play' href={PLAY_STORE_URL} variant='onLight' surface='banner' />
      </div>
    </section>
  );
}

/**
 * The download-the-app modal (title, blurb, store buttons), controlled via `open`. Radix portals
 * outside the layout root, so re-declare the brand vars on DialogContent.
 */
export function AppDownloadModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const group = useEnrollGroup();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        style={enrollBrandVars(group.primaryColor)}
        className={`max-w-md gap-5 p-8 ${POSTHOG_UNMASK_CLASS}`}
      >
        <DialogHeader>
          <DialogTitle className='text-[var(--enroll-ink)]'>
            Download the Clever Health mobile app
          </DialogTitle>
          <DialogDescription className='pt-2 pb-2 pr-4 leading-relaxed'>
            View your member ID cards, start a virtual visit, and access prescription savings —
            right from your phone.
          </DialogDescription>
        </DialogHeader>
        <div className='flex flex-wrap gap-3'>
          <StoreButton label='App Store' href={APP_STORE_URL} variant='onLight' surface='modal' />
          <StoreButton
            label='Google Play'
            href={PLAY_STORE_URL}
            variant='onLight'
            surface='modal'
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Inline blue-underline text that opens the download modal. Rendered by the <app> rich-text tag. */
export function AppLink({children}: {children: ReactNode}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type='button'
        onClick={() => setOpen(true)}
        className='text-blue-600 underline underline-offset-2 hover:text-blue-700'
      >
        {children}
      </button>
      <AppDownloadModal open={open} onOpenChange={setOpen} />
    </>
  );
}

const STEP_PATHS = new Set<string>(Object.values(STEP));

/**
 * Global floating "download the app" button (bottom-right). Fades/slides in a couple seconds after
 * load, opens the modal on click, and can be dismissed. Hidden during the enrollment flow. Mounted
 * once in EnrollLayout, so both the appearance timer and a dismissal persist across in-app
 * navigation and reset on a full refresh.
 */
export function AppDownloadFab() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  const lastSegment = location.pathname.split('/').filter(Boolean).pop() ?? '';
  // Hidden across the active enrollment steps, but shown on the post-enroll confirmation page.
  const inFlow = STEP_PATHS.has(lastSegment) && lastSegment !== STEP.Confirmation;
  const show = !inFlow && !dismissed && visible;

  return (
    <>
      {show && (
        <div className='fixed bottom-5 right-5 z-40 animate-in fade-in slide-in-from-bottom-8 zoom-in-95 duration-500'>
          <div className='flex items-center gap-1 rounded-full bg-[var(--enroll-accent)] py-2 pl-4 pr-2 text-white shadow-lg shadow-black/20'>
            <button
              type='button'
              onClick={() => setOpen(true)}
              className='flex items-center gap-2 text-sm font-semibold'
            >
              <Smartphone className='h-4 w-4' />
              Download the app
            </button>
            <button
              type='button'
              onClick={() => setDismissed(true)}
              aria-label='Dismiss'
              className='ml-1 inline-flex h-6 w-6 items-center justify-center rounded-full transition-colors hover:bg-white/20'
            >
              <X className='h-4 w-4' />
            </button>
          </div>
        </div>
      )}
      <AppDownloadModal open={open} onOpenChange={setOpen} />
    </>
  );
}
